<script setup lang="ts">
import type { CrashCandle } from '~/types/crash-pre-game';

type Props = {
  /** Velas na ordem que o core mandou: índice 0 = última rodada. */
  candles: CrashCandle[];
  isLoading?: boolean;
};

const { candles, isLoading = false } = defineProps<Props>();

// No mobile a grade tem 4 colunas: mostrar as 48 de uma vez empurraria o resto
// da tela pra baixo de 12 linhas de vela. Começa em 20 (5 linhas) com o botão
// abrindo o resto. No desktop são 12 por linha — as 48 fecham em 4 linhas
// certinhas — e o corte não existe.
const COLLAPSED = 20;

const isExpanded = ref(false);

const rootRef = ref<HTMLElement | null>(null);

// Encolher de 47 pra 20 tira ~7 linhas de grade de baixo do jogador: ele fica
// olhando pro que antes era o meio da tela e perde a referência do bloco. Só na
// volta pro fechado — expandir cresce PRA BAIXO, e aí o topo não se move.
//
// Sobe até o topo do accordion (o casco se marca com `data-crash-block`), não
// até a grade: o título é o que devolve a orientação. O scrollIntoView resolve
// o container sozinho — no mobile é o .mgp-stack do sheet, no desktop é a
// janela.
async function collapse() {
  isExpanded.value = false;

  // Espera a grade encolher de verdade: disparado antes, o scroll suave
  // animaria mirando o layout antigo e a página encurtaria no meio do caminho.
  await nextTick();

  const block = rootRef.value?.closest('[data-crash-block]');

  if (!block) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  block.scrollIntoView({
    behavior: reduced ? 'auto' : 'smooth',
    block: 'start',
  });
}

function toggleExpanded() {
  if (isExpanded.value) {
    collapse();

    return;
  }

  isExpanded.value = true;
}

const { isMobile } = useLayout();

const isTruncated = computed(
  () => isMobile.value && !isExpanded.value && candles.length > COLLAPSED,
);

const visibleCandles = computed(() =>
  isTruncated.value ? candles.slice(0, COLLAPSED) : candles,
);

const canToggle = computed(() => isMobile.value && candles.length > COLLAPSED);

const toggleLabel = computed(() =>
  isExpanded.value ? 'Ver menos' : 'Ver mais',
);

// Horário da rodada no fuso do JOGADOR. Sem segundos aqui — na grade o que
// importa é situar a rodada no tempo, e "15:53" repetido em duas velas seguidas
// não atrapalha (quem quer o segundo exato olha a vela do topo).
function formatRoundTime(roundTime: string): string {
  if (!roundTime) return '';

  const parsed = Date.parse(roundTime);

  if (Number.isNaN(parsed)) return '';

  return new Date(parsed).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// A célula que ACABOU de entrar ganha uma classe e, com ela, a animação de
// entrada. A marca é o `roundTime` da vela do topo: o polling devolve o array
// inteiro a cada tick, e comparar a identidade do array acusaria "novidade" em
// toda resposta, inclusive nas que vieram iguais.
//
// Nada de `transition` aqui: o reset global em assets/css/main.scss mata
// `transition` no app inteiro (`*:not(.swiper-wrapper)…`), e só `animation`
// sobrevive.
const newestRoundTime = ref<string | null>(null);

watch(
  () => candles[0]?.roundTime ?? null,
  (roundTime, previous) => {
    // `previous` indefinido é a primeira leitura: a grade nasce cheia e animar
    // as 48 de uma vez não é "entrou uma vela nova", é fogos de artifício.
    if (!roundTime || !previous) return;

    newestRoundTime.value = roundTime;
  },
);

// A marca se apaga quando a animação termina. Sem isso, a célula do topo ficaria
// com a classe já aplicada quando a PRÓXIMA vela chegasse — e animação em CSS só
// dispara quando a classe ENTRA, então a segunda vela entraria sem efeito.
function clearFresh(): void {
  newestRoundTime.value = null;
}

// Sempre 2 casas, como o jogo mostra: "1.05X", "15.72X".
function formatMultiplier(multiplier: number): string {
  return `${multiplier.toFixed(2)}X`;
}
</script>

<template>
  <div ref="rootRef">
    <!-- A legenda traduz a cor em faixa. Sem ela, "azul" não quer dizer nada
         pra quem abriu a tela pela primeira vez. -->
    <ul class="crash-legend">
      <li class="crash-legend__item">
        <i
          aria-hidden="true"
          class="crash-legend__sw crash-legend__sw--blue"
        />
        Menos de 2X
      </li>
      <li class="crash-legend__item">
        <i
          aria-hidden="true"
          class="crash-legend__sw crash-legend__sw--purple"
        />
        De 2X a 10X
      </li>
      <li class="crash-legend__item">
        <i
          aria-hidden="true"
          class="crash-legend__sw crash-legend__sw--pink"
        />
        10X ou mais
      </li>
    </ul>

    <div
      v-if="isLoading"
      class="crash-grid"
    >
      <div
        v-for="i in 20"
        :key="i"
        class="crash-grid__cell crash-grid__cell--skeleton skeleton-loading"
      />
    </div>

    <!-- Preenchida da esquerda pra direita, linha por linha: a mais recente é a
         primeira célula. A ordem é a do core e não é reordenada aqui. -->
    <div
      v-else
      class="crash-grid"
    >
      <div
        v-for="(candle, index) in visibleCandles"
        :key="`${candle.roundTime}-${index}`"
        class="crash-grid__cell"
        :class="[
          `crash-grid__cell--${candle.color}`,
          {
            'crash-grid__cell--fresh':
              index === 0 && candle.roundTime === newestRoundTime,
          },
        ]"
        @animationend="clearFresh"
      >
        <div class="crash-grid__v">{{
          formatMultiplier(candle.multiplier)
        }}</div>
        <div class="crash-grid__t">{{ formatRoundTime(candle.roundTime) }}</div>
      </div>
    </div>

    <button
      v-if="canToggle && !isLoading"
      :aria-expanded="isExpanded"
      class="crash-more"
      type="button"
      @click="toggleExpanded"
    >
      {{ toggleLabel }}
    </button>
  </div>
</template>

<style scoped>
.crash-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
}

@media (min-width: 1024px) {
  .crash-legend {
    gap: 26px;
  }
}

.crash-legend__item {
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}

@media (min-width: 1024px) {
  .crash-legend__item {
    gap: 9px;
  }
}

.crash-legend__sw {
  flex: none;
  width: 11px;
  height: 11px;
  border-radius: 4px;
}

.crash-legend__sw--blue {
  background: #37c6f0;
  box-shadow: 0 0 11px rgba(55, 198, 240, 0.95);
}

.crash-legend__sw--purple {
  background: #a560f5;
  box-shadow: 0 0 11px rgba(165, 96, 245, 0.95);
}

.crash-legend__sw--pink {
  background: #ec5a98;
  box-shadow: 0 0 11px rgba(236, 90, 152, 0.95);
}

/* 4 colunas no mobile, 6 no meio-termo e 12 no desktop — 12 × 4 fecha as 48
   velas do core em quatro linhas cheias, sem sobra na última. */
.crash-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
}

@media (min-width: 640px) {
  .crash-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
  }
}

@media (min-width: 1024px) {
  .crash-grid {
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 5px;
  }
}

.crash-grid__cell {
  padding: 10px 9px 9px;
  border: 1px solid transparent;
  border-radius: 12px;
}

/* Doze colunas apertam a célula; o respiro sai da lateral, que é folga, e não
   do corpo do multiplicador, que é o dado. */
@media (min-width: 1024px) {
  .crash-grid__cell {
    padding: 10px 6px 9px;
  }
}

.crash-grid__cell--skeleton {
  height: 54px;
  border-radius: 12px;
}

.crash-grid__v {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.crash-grid__t {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* Quem decide a cor de cada vela é o core (blue < 2x ≤ purple < 10x ≤ pink);
   aqui só existe a tradução cor → estilo. */
.crash-grid__cell--blue {
  --crash-candle-rgb: 55, 198, 240;

  background: rgba(55, 198, 240, 0.14);
  border-color: rgba(55, 198, 240, 0.4);
}

.crash-grid__cell--blue .crash-grid__v {
  color: #37c6f0;
}

.crash-grid__cell--purple {
  --crash-candle-rgb: 165, 96, 245;

  background: rgba(165, 96, 245, 0.15);
  border-color: rgba(165, 96, 245, 0.4);
}

.crash-grid__cell--purple .crash-grid__v {
  color: #c79bff;
}

.crash-grid__cell--pink {
  --crash-candle-rgb: 236, 90, 152;

  background: rgba(236, 90, 152, 0.18);
  border-color: rgba(236, 90, 152, 0.45);
}

.crash-grid__cell--pink .crash-grid__v {
  color: #ff8fbe;
}

/* A vela nova não "aparece": ela entra de cima, assenta com um respiro e o
   brilho da PRÓPRIA faixa se abre em anel uma vez só — daí o `--crash-candle-rgb`
   de cada cor. Só a primeira célula anima: as outras trocam de conteúdo no mesmo
   lugar, e piscar todas viraria ruído a cada tick. */
@keyframes crash-candle-in {
  from {
    opacity: 0;
    transform: scale(0.82) translateY(-8px);
  }

  50% {
    opacity: 1;
    transform: scale(1.05) translateY(0);
  }

  72% {
    transform: scale(0.985);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Anel que nasce colado na borda e se dissolve pra fora, com um halo suave por
   baixo. Os dois terminam transparentes, então a célula fica exatamente como as
   vizinhas quando acaba. */
@keyframes crash-candle-halo {
  from {
    box-shadow:
      0 0 0 0 rgba(var(--crash-candle-rgb), 0.55),
      0 0 20px 2px rgba(var(--crash-candle-rgb), 0.45);
  }

  to {
    box-shadow:
      0 0 0 12px rgba(var(--crash-candle-rgb), 0),
      0 0 20px 2px rgba(var(--crash-candle-rgb), 0);
  }
}

/* O número chega aceso e volta ao tom da faixa. `brightness` não depende da cor,
   então vale pras três sem uma regra por cor. */
@keyframes crash-candle-value {
  from {
    filter: brightness(2.1);
  }

  to {
    filter: brightness(1);
  }
}

/* As duas animações da célula têm a MESMA duração de propósito: a classe cai no
   primeiro `animationend`, e uma mais longa que a outra seria cortada no meio. */
.crash-grid__cell--fresh {
  /* A célula nova é a PRIMEIRA do grid, e irmã que vem depois pinta por cima:
     sem subir a pilha, o anel sumiria por baixo da vela ao lado. */
  position: relative;
  z-index: 1;
  animation:
    crash-candle-in 560ms cubic-bezier(0.22, 1, 0.36, 1) both,
    crash-candle-halo 560ms ease-out both;
}

.crash-grid__cell--fresh .crash-grid__v {
  animation: crash-candle-value 560ms ease-out both;
}

/* Quem pediu menos movimento recebe a vela pronta, sem salto nem brilho. */
@media (prefers-reduced-motion: reduce) {
  .crash-grid__cell--fresh,
  .crash-grid__cell--fresh .crash-grid__v {
    animation: none;
  }
}

.crash-more {
  width: 100%;
  margin-top: 12px;
  padding: 14px;
  border: 1px solid #1c2740;
  border-radius: 12px;
  background: none;
  color: #37c6f0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
</style>
